import React from 'react';

const Screenshots: React.FC = () => {
  const screenshots = [
    {
      src: '/assets/screenshots/01.png',
      alt: 'Interface de jeu principale - Écran d\'accueil',
      title: 'Écran d\'accueil',
      description: 'Interface moderne avec navigation intuitive'
    },
    {
      src: '/assets/screenshots/02.png',
      alt: 'Écran de règles et tutoriel',
      title: 'Règles du jeu',
      description: 'Apprenez à jouer avec le tutoriel'
    },
    {
      src: '/assets/screenshots/03.png',
      alt: 'Interface de jeu - Partie en cours',
      title: 'Partie en cours',
      description: 'Interface de jeu avec cartes et scores'
    },
    {
      src: '/assets/screenshots/04.png',
      alt: 'Menu principal du jeu',
      title: 'Menu principal',
      description: 'Navigation complète avec toutes les options'
    },
    {
      src: '/assets/screenshots/05.png',
      alt: 'Début de la partie',
      title: 'Début de la partie',
      description: 'Début de la partie'
    },
    {
      src: '/assets/screenshots/06.png',
      alt: 'Interface de jeu avancée',
      title: 'Mode avancé',
      description: 'Fonctionnalités avancées du jeu'
    }
  ];

  return (
    <section id="screenshots" className="section-padding bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Découvrez le jeu
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une interface moderne et intuitive pour une expérience de jeu optimale
          </p>
        </div>

        {/* Banner with game preview */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-8 text-white text-center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">Prêt à jouer ?</h3>
                <p className="text-lg mb-6 opacity-90">
                  Téléchargez maintenant et commencez votre aventure stratégique !
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.djammadev.game.i151"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-primary-600 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    Android
                  </a>
                  <a
                    href="https://apps.apple.com/fr/app/i151/id6748642726?l=en-GB"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-primary-600 font-semibold py-3 px-6 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                    </svg>
                    iOS
                  </a>
                </div>
              </div>
              <div className="relative">
                <img 
                  src="/assets/description.png" 
                  alt="Aperçu du jeu Jeu 151" 
                  className="w-full max-w-sm mx-auto rounded-xl shadow-2xl"
                />
                <div className="absolute -top-4 -right-4 bg-yellow-500 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl shadow-lg">
                  ⭐
                </div>
                <div className="absolute -bottom-4 -left-4 bg-primary-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg shadow-lg">
                  🎴
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Screenshots Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {screenshots.map((screenshot, index) => (
            <div key={index} className="card overflow-hidden group">
              <div className="relative">
                <img 
                  src={screenshot.src} 
                  alt={screenshot.alt}
                  className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {screenshot.title}
                </h3>
                <p className="text-gray-600">
                  {screenshot.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional screenshots showcase */}
        <div className="mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Plus de captures d'écran
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative group">
                <img 
                  src="/assets/screenshots/07.png" 
                  alt="Interface de jeu avancée" 
                  className="w-full rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">Interface avancée</span>
                </div>
              </div>
              <div className="relative group">
                <img 
                  src="/assets/screenshots/08.png" 
                  alt="Fonctionnalités spéciales" 
                  className="w-full rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">Fonctionnalités spéciales</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Game Info */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Une expérience de jeu unique
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Plongez dans l'univers du Jeu 151 avec une interface soigneusement 
                conçue pour offrir la meilleure expérience de jeu possible. 
                Des animations fluides, des effets sonores immersifs et une 
                navigation intuitive vous permettront de vous concentrer sur 
                l'essentiel : la stratégie.
              </p>
            </div>

            {/* Game Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6">
                <div className="text-3xl mb-3">🎨</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Design moderne
                </h4>
                <p className="text-gray-600 text-sm">
                  Interface épurée avec des couleurs harmonieuses et des animations fluides
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                <div className="text-3xl mb-3">⚡</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Performance optimale
                </h4>
                <p className="text-gray-600 text-sm">
                  Chargement rapide et fonctionnement fluide sur tous les appareils
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                <div className="text-3xl mb-3">🎮</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Contrôles intuitifs
                </h4>
                <p className="text-gray-600 text-sm">
                  Navigation simple et gestes naturels pour une prise en main immédiate
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                <div className="text-3xl mb-3">📱</div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Responsive design
                </h4>
                <p className="text-gray-600 text-sm">
                  Adaptation parfaite sur mobile, tablette et desktop
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Screenshots; 